import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import { HabilitationDTO } from '../../models/habilitation.model';
import { HabilitationDto, HabilitationLiberalDto, HabilitationStructureDto, ResponseHabStructureDto } from '../../models/habilitation-dto.model';
import { HabilitationFermeture } from '../../models/habilitation-fermeture.model';
import { HabilitationStatesEnum } from '../../models/habilitation-type.model';
import { HabilitationType } from '../../models/habilitation-type.model';
import { ParametreContextuelDto } from '../../models/parametre-contextuel-dto.model';
import { RoleMetierDto } from '../../models/role-metier-dto.model';
import { UserInfoService } from '../user-info/user-info.service';

@Injectable({
  providedIn: 'root',
})
export class HabilitationSgcService {
  constructor(
    private readonly http: HttpClient,
    private readonly userInfo: UserInfoService
  ) {}

  getAllHabilitationByPersonne(): Observable<HabilitationDto[]> {
    const auth = this.userInfo.getAuth();
    const params = new HttpParams()
      .set('typePersonne', auth.item.userId.substring(0, 1))
      .set('idPersonne', auth.item.userId.substring(1))
      .set('rolesPersonne', 'MANDATAIRE, REPRESENTANT_LEGAL, DELEGUE')
      .set('statut', HabilitationStatesEnum.OUVERT)
      .set('isTest', 'false');

    return this.http.get<HabilitationDto[]>(
      `${environment.API_URL}habilitations/habilitation/getAllByPersonne`,
      { params }
    );
  }

  getAllHabilitationsLiberales(): Observable<HabilitationLiberalDto[]> {
    const auth = this.userInfo.getAuth();
    const params = new HttpParams().set('idNatPS', auth.item.userId);

    return this.http.get<HabilitationLiberalDto[]>(
      `${environment.API_URL}v2/habilitationsbyIdNatPS`,
      { params }
    );
  }

  getAllHabilitationsbyIdNatStruct(critere: HabilitationStructureDto): Observable<ResponseHabStructureDto[]> {
    let params = new HttpParams().set('idNatStruct', critere?.idNatStruct);

    if (critere?.profils?.length > 0) {
      params = params.append('profils', critere.profils.join(', '));
    }

    return this.http.get<ResponseHabStructureDto[]>(`${environment.API_URL}v2/habilitationsbyIdNatStruct`, { params });
  }

  /**
   * Requete POST au service de création d'un lot d'habilitations.
   * @param habilitations List of habilitations to save
   * @param isDuplication True if habilitations must have to be duplicated
   */
  saveHabilitations(habilitations: HabilitationDTO[], isDuplication: boolean): Observable<any> {
    return this.http.post(
      `${environment.API_URL}cpx-habilitations/habilitation/saveHabilitations?isDuplication=${isDuplication}`,
      habilitations,
      { observe: 'response' }
    );
  }

  /**
   * Requete POST au service de création d'un lot d'habilitations (Personne Physique).
   * @param habilitations List of habilitations to save
   */
  saveHabilitationsGAEAndGARS(habilitations: HabilitationDTO[]): Observable<any> {
    return this.http.post(`${environment.API_URL}cpx-habilitations/habilitationGAE/saveHabilitationsGAE`, habilitations, { observe: 'response' });
  }

  /**
   * Requete POST au service de création d'un lot d'habilitations (Personne Morale).
   * @param habilitations List of habilitations to save
   */
  saveHabilitationsPM(habilitations: HabilitationDTO[]): Observable<any> {
    return this.http.post(`${environment.API_URL}cpx-habilitations/habilitationPM/saveHabilitationsPM`, habilitations, { observe: 'response' });
  }

  /**
   * Requete POST au service de duplication d'un lot d'habilitations GAE ou GARS.
   * @param habilitation Habilitation
   */
  saveHabilitationDuplicates(habilitation: HabilitationDTO): Observable<any> {
    return this.http.post(`${environment.API_URL}habilitations/habilitation/saveHabilitationsDuplicates`, habilitation);
  }

  /**
   * Close an habilitation:
   *  - GAE
   *  - structure (Habilitation Personne Morale)
   *  - others
   * @param habilitationFermeture HabilitationFermeture object
   * @param typeHabilitation Habilitation type
   * @returns Observable with the API answer
   */
  closeHabilitation(habilitationFermeture: HabilitationFermeture, typeHabilitation: HabilitationType | string): Observable<any> {
    if ([HabilitationType.DELEGUE_PM.toString(), HabilitationType.CL_PM.toString()].includes(typeHabilitation)) {
      return this.http.put(`${environment.API_URL}cpx-habilitations/habilitationPM/${habilitationFermeture.id}/close`, {});
    } else {
      const url = (typeHabilitation !== HabilitationType.CL) ? 'GAE' : '';
      return this.http.post(`${environment.API_URL}habilitations/habilitation${url}/close`, habilitationFermeture);
    }
  }

  /**
   * Close an habilitation list of CL = Admin technique de prod = GEST_PROD
   * @param habilitations Habilitation list to delete
   * @returns Observable with the API answer
   */
  public closeHabilitationList(habilitations: HabilitationFermeture[]): Observable<any> {
    return this.http.post(`${environment.API_URL}habilitations/habilitation/closeHabilitations`, habilitations);
  }

  abandonHabilitation(idHabilitation: string): Observable<any> {
    const params = new HttpParams().set('id', idHabilitation);
    return this.http.get(`${environment.API_URL}habilitations/habilitation/abandon`, { params });
  }

  public getAllHabilitationsByStructurePerimetreContractuelAndRoleMetier(
    cleStructurePerimetreContractuel: string[], statut: string, roles?: string[]
  ): Observable<HabilitationDto[]> {

    const request = {
      clesStructurePerimetreContractuel: cleStructurePerimetreContractuel,
      statut: statut,
      rolesPersonne: roles ? roles.toString() : '',
      isTest: false,
    };

    return this.http.post<HabilitationDto[]>(`${environment.API_URL}habilitations/habilitation/getByStructurePerimetreContractuelListAndRoleMetier`, request);
  }

  public getAllHabilitationsPMByStructurePerimetreContractuelAndRolesMetierAndStatuts(
    clesStructurePerimetreContractuel: string[], statuts: string[], roles?: string[]
  ): Observable<HabilitationDto[]> {

    const request = {
      clesStructurePerimetreContractuel: clesStructurePerimetreContractuel,
      statuts: statuts,
      roles: roles,
      isTest: false,
    };

    return this.http.post<HabilitationDto[]>(`${environment.API_URL}habilitations/habilitationPM/getByStructurePerimetreContractuelListAndRolesMetierAndStatuts`, request);
  }

  public getListParametresContextuelByCle(clesParametreContextuel: string[]): Observable<Map<string, ParametreContextuelDto>> {
    return this.http.post<Map<string, ParametreContextuelDto>>(`${environment.API_URL}contrats/parametreContextuel/rechercherByCles`, clesParametreContextuel);
  }

  public getAllHabilitationsByListClesParametreContextuel(clesParametreContextuel: string[], isTest: boolean): Observable<any> {
    const params = new HttpParams()
      .set('clesParametreContextuel', clesParametreContextuel ? clesParametreContextuel.toString() : '')
      .set('isTest', isTest.toString());

    return this.http.get(`${environment.API_URL}habilitations/habilitation/getByClesParametreContextuel`, { params });
  }

  /**
   * Requete GET au service de récuperation des roles metiers PORTEE_STRUCTURE de l'utilisateur.
   */
  public getAllRoleMetierPorteeStructure(): Observable<RoleMetierDto[]> {
    return this.http.get<RoleMetierDto[]>(`${environment.API_URL}habilitations/roleMetier/getAllRoleMetierPorteeStructure`);
  }

  /**
   * Retourne le nombre maximum d'habilitations à créer
   */
  public getHabilitationsCreationMaxLength(): Observable<number>{
    return this.http.get<number>(`${environment.API_URL}habilitations/habilitation/creationMaxLength`);
  }

}
